import { Client } from "@/Common/Api/Client";
import { FormFieldRow } from "@/Common/Components/Forms/FormFieldRow";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { Guid } from "@/Common/Models/Guid";
import { IMap } from "@/Common/Models/IMap";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ConstantValues } from "../Constants/ConstantValues";
import { ValueSetType } from "../Constants/ValueSetType";
import { ImmunizationModel } from "../Models/ImmunizationModel";
import { RvqDomainValue } from "../Models/RvqDomainValue";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { DeleteRvqImmunizationRequest } from "../RequestModels/DeleteRvqImmunizationRequest";
import { GetValuesRequest } from "../RequestModels/GetValuesRequest";
import { DeleteRvqImmunizationResponse } from "../ResponseModels/DeleteRvqImmunizationResponse";
import { BaseImmunizationProfileSection, BaseImmunizationProfileSectionState, IBaseImmunizationProfileSectionProps } from "./BaseImmunizationProfileSection";

interface ImmunizationDeletionProp extends IBaseImmunizationProfileSectionProps {
    immunization: ImmunizationModel;
    onVaccinationRemoval: (associatedMedicalItemId?: Guid, deletionDate?: Date) => void;
}

class ImmunizationDeletionState extends BaseImmunizationProfileSectionState {
    public deletionReasonCode: string;
    public deletionDetails: string;
    public reasonValues: IMap<RvqDomainValue>;

    constructor(init?: Partial<ImmunizationDeletionState>) {
        super(init);
    }

    public static default(): ImmunizationDeletionState {
        return new ImmunizationDeletionState({
            ...BaseImmunizationProfileSectionState.default(),
            deletionReasonCode: "",
            deletionDetails: "",
            reasonValues: {}
        });
    }
}

class ImmunizationDeletion extends BaseImmunizationProfileSection<ImmunizationDeletionState, ImmunizationDeletionProp> {
    public constructor(props: ImmunizationDeletionProp) {
        super(props);
        this.state = ImmunizationDeletionState.default();
    }

    protected requestValues = new GetValuesRequest({
        Type: [ValueSetType.DeletionReason]
    });

    public componentDidMount(): void {
        super.componentDidMount(() => this.mutateState(s => s.reasonValues = s.values?.Types[ValueSetType.DeletionReason]));
    }

    protected getBody(): JSX.Element {
        return (
            <div>
                <FormFieldRow
                    labelValueKey="QuebecVaccinationRegistry_DeletionReasonLabel"
                    isEditMode
                >
                    <SelectKVPControl
                        Callback={item => this.mutateState(s => s.deletionReasonCode = item)}
                        AvailableValues={this.deletionReasonOptions}
                        Value={this.state.deletionReasonCode}
                        isInvalid={IsNullOrWhiteSpace(this.state.deletionReasonCode)}
                        IncludeEmptyChoice
                    />
                </FormFieldRow>
                <FormFieldRow
                    labelValueKey="QuebecVaccinationRegistry_DeletionReasonPrecisionLabel"
                    isEditMode
                >
                    <textarea
                        id="immunityProofComments"
                        onChange={(e: any) => {
                            const value = e.target.value;
                            this.mutateState(s => s.deletionDetails = value);
                        }}
                        value={this.state.deletionDetails}
                        className={`form-control textarea-resize-vertical ${(!this.state.deletionDetails && this.state.deletionReasonCode === ConstantValues.OtherReasonCode) ? "invalid" : ""}`}
                        rows={3}
                    />
                </FormFieldRow>
            </div>
        );
    }

    private get deletionReasonOptions(): KeyValuePair<string, string>[] {
        return KeyValuePair.fromMapOfStrings(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.reasonValues, ValueSetType.DeletionReason));
    }

    protected async onSave(): Promise<void> {
        const response = await Client.postAsync<DeleteRvqImmunizationResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_DeleteImmunization,
            new DeleteRvqImmunizationRequest({
                ImmunizationId: this.props.immunization.Id,
                Version: this.props.immunization.Metadata.VersionId,
                DeletionReason: this.state.deletionReasonCode,
                DeletionReasonComment: this.state.deletionDetails,
                TransactionId: this.state.cachedTransactionId
            })
        );

        this.handleRvqResponse(response, () => this.props.onVaccinationRemoval(response?.data?.AssociatedMedicalItemId, response?.data?.DeletionDate));
    }

    protected canSave(): boolean {
        return !!this.state.deletionReasonCode
            && (
                this.state.deletionReasonCode !== ConstantValues.OtherReasonCode
                || !IsNullOrWhiteSpace(this.state.deletionDetails)
            );
    }
}

export { ImmunizationDeletion };
